import { LogicalOperationValueType } from 'Constants/constants'
import { ExamInfoType } from './Exams.types'
import { DifficultyLevel } from 'Constants/tasks.constants'
import { ModelFeedback } from './CustomTask.types'
import {
    AttributeValueTypeDefinitions,
    FileToModelValueType,
    FileValueType,
} from 'Types/Attribute.types'
import { JSONObject, JSONSchemaMetaData } from './Common.types'
import type { CSATFeedbackConfig } from 'Types/CSAT.types'
import { InterfaceConfig } from 'shared/express-interface/core/types'

export enum RoleType {
    ASSISTANT = 'ASSISTANT',
    PROMPTER = 'PROMPTER',
}

export enum SourceType {
    HUMAN = 'HUMAN',
    MODEL = 'MODEL',
    DATASET = 'DATASET',
}

export enum ContainerResponseType {
    CONVERSATION_RESPONSE = 'CONVERSATION_RESPONSE',
}

export type MediaTypeDefinitions = {
    url: string
    label: string
}

export type ValueTypeDefinitions = {
    STRING: string
    INT: number
    DATE: string
    TIME: string
    STRING_ARRAY: string[]
    HTML: string
    RICH_TEXT: string
    MARKDOWN: string
    MULTI_MEDIA_GRID: SingleValueDataType<keyof ValueTypeDefinitions>[][]
    IMAGE: MediaTypeDefinitions
    VIDEO: MediaTypeDefinitions
    AUDIO: MediaTypeDefinitions
    DOWNLOAD_FILE_MAPPING: FileValueType
    JSON: JSONObject
}

export type PromptDataType =
    | string
    | MediaTypeDefinitions
    | SingleValueDataType<keyof ValueTypeDefinitions>[][]

export const DefaultValueTypeDefinitions: Omit<ValueTypeDefinitions, 'INT'> = {
    STRING: '',
    DATE: '',
    TIME: '',
    STRING_ARRAY: [],
    HTML: '',
    RICH_TEXT: '',
    MARKDOWN: '',
    MULTI_MEDIA_GRID: [],
    IMAGE: {
        url: '',
        label: '',
    },
    VIDEO: {
        url: '',
        label: '',
    },
    AUDIO: {
        url: '',
        label: '',
    },
    DOWNLOAD_FILE_MAPPING: {
        download_url: '',
        path: '',
    },
    JSON: {},
}

export type ValueType = keyof ValueTypeDefinitions

export enum InputType {
    SELECT = 'SELECT',
    INT_SCALE = 'INT_SCALE',
    TEXT = 'TEXT',
    STRING_ARRAY = 'STRING_ARRAY',
    MULTI_SELECT = 'MULTI_SELECT',
    INT = 'INT',
    DATE = 'DATE',
    TIME = 'TIME',
    FILE = 'FILE',
    RICH_TEXT = 'RICH_TEXT',
    MARKDOWN = 'MARKDOWN',
}

export type InputTypeToValueTypeMap = {
    [InputType.SELECT]: 'STRING'
    [InputType.INT_SCALE]: 'INT'
    [InputType.TEXT]: 'STRING'
    [InputType.STRING_ARRAY]: 'STRING_ARRAY'
    [InputType.MULTI_SELECT]: 'STRING_ARRAY'
    [InputType.INT]: 'INT'
    [InputType.DATE]: 'DATE'
    [InputType.TIME]: 'TIME'
    [InputType.FILE]: 'DOWNLOAD_FILE_MAPPING'
    [InputType.RICH_TEXT]: 'RICH_TEXT'
    [InputType.MARKDOWN]: 'MARKDOWN'
}

export enum AttributeDefinitionType {
    EXTRA_INFO = 'EXTRA_INFO',
    RESPONSE_RATING = 'RESPONSE_RATING',
    QUALITY_EVALUATION = 'QUALITY_EVALUATION',
    JSON_PATH = 'JSON_PATH',
    TURN_EVALUATION = 'TURN_EVALUATION',
    TARGETED_ATTRIBUTES = 'TARGETED_ATTRIBUTES',
    TURN_TARGETED_ATTRIBUTES = 'TURN_TARGETED_ATTRIBUTES',
}

export enum StatusType {
    ASSIGNED = 'ASSIGNED',
    TO_BE_ASSIGNED = 'TO_BE_ASSIGNED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETE = 'COMPLETE',
    ON_HOLD = 'ON_HOLD',
}

export enum DecisionStatusType {
    ACCEPT = 'ACCEPT',
    REJECT = 'REJECT',
}

export enum ResultType {
    PASS = 'PASSED',
    FAIL = 'FAILED',
}

export enum ContainerType {
    LinearConversation = 'LINEAR_CONVERSATION',
    ConversationResponse = 'CONVERSATION_RESPONSE',
    RatingsOnLinearConversation = 'RATINGS_ON_LINEAR_CONVERSATION_WITH_MULTIPLE_RESPONSES',
    TextAnnotation = 'TEXT_ANNOTATIONS_RESPONSE',
    JSONAnnotation = 'JSON_ANNOTATION_OUTPUT',
    Custom = 'CUSTOM_OUTPUT',
    Conversations = 'MODEL_CONVERSATIONS_OUTPUT',
    Conversationsv2 = 'MODEL_CONVERSATIONS_OUTPUT_V2',
    AgenticAnnotation = 'AGENT_TRAJECTORY_CREATION_OUTPUT',
    CustomInterface = 'CUSTOM_INTERFACE_OUTPUT',
}

export type ContainerTypeMap = {
    [ContainerType.ConversationResponse]: ConversationResponseDataType
    [ContainerType.RatingsOnLinearConversation]: RatingsOnLinearConversationDataType
    [ContainerType.LinearConversation]: LinearConversationsDataType
    [ContainerType.TextAnnotation]: TextAnnotationDataType
    [ContainerType.Custom]: CustomDataType
    [ContainerType.JSONAnnotation]: JSONAnnotationDataType
    [ContainerType.Conversations]: ConversationsDataType
    [ContainerType.Conversationsv2]: Conversationsv2DataType
    [ContainerType.AgenticAnnotation]: AgenticAnnotationDataType
    [ContainerType.CustomInterface]: CustomInterfaceDataType
}

export type SubmissionType<T extends ContainerType, U> = {
    id: string
    time_elapsed_in_seconds: number
    draft_output_container?: ContainerDataType<T, U>
}

export type ConversationNode = {
    id: string
    role: RoleType
    source: SourceType
    content:
        | ValueDataType<'STRING'>
        | ValueDataType<'MULTI_MEDIA_GRID'>
        | ValueDataType<'HTML'>
        | ValueDataType<'IMAGE'>
}

export type JSONAnnotationNode = {
    id: string
    role: RoleType
    source: SourceType
    content: UpdatedValueDataType<'JSON'>
}

export type LinearConversationsDataType = {
    conversation_nodes?: ConversationNode[]
    responses?: ConversationNode[]
    content_entity?: {
        content: {
            value: string | SingleValueDataType<keyof ValueTypeDefinitions>[][]
            value_id: string
            value_type: 'HTML'
        }
        id: string
    }
    main_info?: {
        value_type: 'JSON'
        value: {
            prompt: string
            prompt_instruction?: string
            version?: number
        }
    }
    json_annotation?: JSONAnnotationNode
    custom_interface?: JSONAnnotationNode
}

export type CustomInterfaceConfig = {
    id: string
    version_id: string
    config: InterfaceConfig
}

export type TextAnnotationDataType = {
    response: {
        id: string
        version_id?: string | null
        extra_info?: UpdatedAttributesData
        text_annotations: TextAnnotationsListType[]
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: UpdatedQCAttributesData
        } | null
    }
}

export type JSONAnnotationDataType = {
    response: {
        id: string
        version_id?: string | null
        extra_info?: UpdatedAttributesData
        json_annotations?: JSONAnnotationData
        role: RoleType
        source: SourceType
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: UpdatedQCAttributesData
        } | null
    }
}

export type ConversationMainInfoType = {
    value: {
        turns: {
            external_model_response: string
            prompt: string
            prompt_rounds: {
                responses_list: string[]
            }[]
        }[]
        turn_annotations: {
            attributes: AttributesData
        }[]
    }
    value_type: 'JSON'
}

export type ConversationsDataType = {
    response: {
        id: string
        is_complete: boolean
        version_id?: string | null
        extra_info?: UpdatedAttributesData
        main_info: ConversationMainInfoType
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: UpdatedQCAttributesData
        } | null
    }
}

export type Conversationsv2MainInfoType = {
    value: {
        partially_saved_prompt?: string
        turns_data: {
            prompt: string
            external_model_response: JSONObject
            retry_responses: object[]
            media_files: {
                download_url: string
                path: string
                file_id: string
            }[]
        }[]
        json_attributes: ConversationJSONAnnotationData
    }
    value_type: 'JSON'
}

export type Conversationsv2DataType = {
    response: {
        id: string
        is_complete: boolean
        version_id?: string | null
        extra_info?: UpdatedAttributesData
        main_info: Conversationsv2MainInfoType
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: UpdatedQCAttributesData
        } | null
    }
}

export type TextAnnotationsListType = {
    target: {
        entity_type: string
        entity_id: string
        field_path: string
        entity_source: string
    }
    annotations: {
        value: {
            id: string
            start_index: number
            end_index: number
            class_name: string
            extra_info: {
                comment_attribute_name: {
                    value: string
                    value_type: 'STRING'
                }
            }
        }
        value_type: 'TEXT_ANNOTATION'
    }[]
}

export type SupplementDataType = {
    schema_version: number
    step_name: string
    comment: ValueDataType<'STRING'> | UpdatedValueDataType<'STRING'>
    confidence_level?: string
}

// TODO - Rename to Attributes Data after we migrate from the old AttributesData type
export type UpdatedAttributesData = {
    [attribute_name: string]: UpdatedValueDataType<keyof AttributeValueTypeDefinitions>
}

export type AttributesData = {
    [attribute_name: string]: ValueDataType<keyof AttributeValueTypeDefinitions>
}

export type QCAttributesData = {
    [attrKey: string]: {
        data: SingleValueDataType<'STRING' | 'INT'>
    }
}

export type UpdatedQCAttributesData = {
    [attrKey: string]: SingleValueDataType<'STRING' | 'INT'>
}

export type RatingsAttributesData = {
    ratings: AttributesData
    response_id: string
}

export type JSONAnnotationData = {
    [key: string]: { attributes: UpdatedAttributesData }
}

export type ConversationJSONAnnotationData = {
    [key: string]: UpdatedAttributesData
}

export type ToolMetaDataValue = {
    input_params: JSONObject
    output: JSONObject
    name: string
}

export type ToolMetaData = {
    tool_run_info: {
        data?: {
            value: ToolMetaDataValue
            value_type: 'JSON'
        }
        value?: ToolMetaDataValue
        value_type?: 'JSON'
        value_id: string
    }
}

export type AgenticAnnotationDataType = {
    response: {
        id: string
        version_id: string | null
        extra_info: AttributesData | UpdatedAttributesData
        is_complete: boolean
        tool_meta_data: ToolMetaData[]
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: QCAttributesData | UpdatedQCAttributesData
        } | null
    }
}

export type CustomInterfaceDataType = {
    response: {
        id?: string
        version_id: string | null
        role: RoleType
        source: SourceType
        state: {
            value: Record<string, unknown>
            value_type: 'JSON'
        }
        extra_info?: AttributesData | UpdatedAttributesData
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: QCAttributesData | UpdatedQCAttributesData
        } | null
    }
}

export type ConversationResponseDataType = {
    response: {
        id?: string
        version_id: string | null
        role: RoleType
        source: SourceType
        content: ValueDataType<'HTML'>
        extra_info?: AttributesData | UpdatedAttributesData
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: QCAttributesData | UpdatedQCAttributesData
        } | null
    }
}

export type RatingsOnLinearConversationDataType = {
    id?: string
    version_id: string | null
    role: RoleType
    source: SourceType
    extra_info?: AttributesData
    responses_ratings?: RatingsAttributesData[]
    quality_check: {
        evaluation_on_version_id: {
            [versionId: string]: QCAttributesData
        } | null
    }
}

export type InputDefinition =
    | SelectInputDefinition
    | MultiSelectInputDefinition
    | NumberScaleInputDefinition
    | StringArrayInputDefinition
    | TextInputDefinition
    | NumberInputDefinition
    | FileInputDefinition
    | DateInputDefinition
    | TimeInputDefinition
    | RichTextDefinition
    | MarkdownDefinition

export type ValueDefinition = {
    value_type: ValueType
}

export type VisibilityConditionType = {
    operator: LogicalOperationValueType
    depends_on_attribute_version_id: string
    value: number | string[] | [number, number] | string
}

export type TaskAttribute = InputTypeToTaskAttribute[keyof InputTypeToTaskAttribute]

type InputTypeToTaskAttribute = {
    [K in keyof InputTypeToValueTypeMap]: {
        input_definition: Extract<InputDefinition, { input_type: K }>
        value_definition: { value_type: InputTypeToValueTypeMap[K] }
    } & TaskAttributeBase
}

type TaskAttributeBase = {
    attribute_id: string
    version_id: string
    name: string
    label: string
    type: AttributeDefinitionType
    description?: string
    visibility_condition?: VisibilityConditionType
    is_value_editable: boolean
    linked_to_path?: string
}

export type TaskClasses = {
    class_id: string
    color: string
    description: string
    is_mutable: true
    label: string
    name: string
    prev_version_id: string | null
    project_id: string
    version_id: string
}

export type SelectInputDefinition = {
    options: {
        label: string
        value: string
    }[]
    input_type: InputType.SELECT
    is_optional: boolean
    component_type: 'DROPDOWN' | 'RADIO_BUTTON' | 'SLIDER'
}

export type MultiSelectInputDefinition = {
    options: {
        label: string
        value: string
    }[]
    input_type: InputType.MULTI_SELECT
    is_optional: boolean
    component_type: 'CHECKLIST' | 'DROPDOWN'
}

export type NumberScaleInputDefinition = {
    max: number
    min: number
    interval: number
    input_type: InputType.INT_SCALE
    is_optional: boolean
}

export type StringArrayInputDefinition = {
    input_type: InputType.STRING_ARRAY
    is_optional: boolean
}

export type TextInputDefinition = {
    input_type: InputType.TEXT
    is_optional: boolean
    max_length: number
    placeholder: string
}

export type NumberInputDefinition = {
    input_type: InputType.INT
    is_optional: boolean
}

export type DateInputDefinition = {
    input_type: InputType.DATE
    is_optional: boolean
}

export type TimeInputDefinition = {
    input_type: InputType.TIME
    is_optional: boolean
}

export type FileInputDefinition = {
    input_type: InputType.FILE
    is_optional: boolean
    max_size_in_gb: number
}

export type RichTextDefinition = {
    input_type: InputType.RICH_TEXT
    is_optional: boolean
    grammar: {
        is_enabled: boolean
        custom_dictionary_id: string
        lang: string
        proofread_dialog: boolean
        settings: boolean
    }
}

export type MarkdownDefinition = {
    input_type: InputType.MARKDOWN
    is_optional: boolean
}

export type InstructionType = {
    label: string
    content: string
}

export enum UseCaseTypes {
    SFT_GENERATION_RESPONSE_GENERATION = 'sft:generation:response-generation',
    SFT_GENERATION_RESPONSE_GENERATION_RATING_USECASE = 'sft:generation:response-generation-rating-usecase',
    SFT_GENERATION_RESPONSE_GENERATION_SUMMARISATION_USECASE = 'sft:generation:response-generation-summarisation-usecase',
    RLHF_RATING_RESPONSE_RATING = 'rlhf:rating:response-rating',
    TEXT_ANNOTATION = 'text-annotation',
    CUSTOM = 'custom',
    JSON_ANNOTATION = 'json-annotation',
    MODEL_CONVERSATIONS = 'model-conversations',
    MODEL_CONVERSATIONS_V2 = 'model-interactions-v2',
    AGENTIC_TRAJECTORY_CREATION = 'agent-trajectory-creation',
    CUSTOM_INTERFACE = 'custom-interface',
}

export enum ContextType {
    NormalTask = 'NORMAL_TASK',
    ExamTask = 'EXAM_TASK',
}

export enum EditorType {
    RichText = 'richtext',
    MarkdownEditor = 'markdown',
}

export type ContextConfigType =
    | {
          type: ContextType.ExamTask
          metaData: { isTrainingMode: boolean; examInfo?: ExamInfoType; isArchived?: boolean }
      }
    | { type: ContextType.NormalTask; metaData?: undefined }

export type TaskType<T extends ContainerType, U> = {
    id: string
    type: UseCaseTypes
    job_id: string
    instructions: InstructionType[]
    step_type: string
    json_schema_metadata?: {
        metadata: JSONSchemaMetaData
    }
    task_specific_instructions: string
    attributes: TaskAttribute[]
    classes: TaskClasses[]
    mcp_tools: MCPTool[]
    config: {
        text_annotations_config?: {
            are_comments_enabled: boolean
            are_comments_optional: boolean
            is_overlapping_allowed: boolean
        }
        editor_config?: {
            type: EditorType
        }
        model_request_config?: {
            model_response_mapping_key: string
            stream_response_mapping_key: string | null
        }
        paste_functionality_config?: {
            is_enabled: boolean
        }
        skip_task_config?: {
            is_enabled: boolean
            skip_reasons?: string[]
            is_reason_optional?: boolean
        }
        reject_task_config?: {
            is_enabled: boolean
            rejection_reasons?: string[]
            is_reason_optional?: boolean
        }
        feedback_config?: CSATFeedbackConfig
        save_for_later_config?: {
            is_enabled: boolean
            max_tasks_per_user: number
        }
        response_generation_config?: {
            max_allowed_times: number | undefined
        }
        turn_config?: {
            max_allowed_turns: number | undefined
        }
        activity_check_config?: {
            is_enabled: boolean
            duration_in_minutes: number
        }
    }
    custom_interface?: CustomInterfaceConfig
    input_container: ContainerDataType<
        ContainerType.LinearConversation,
        LinearConversationsDataType
    >
    output_start_state_container?: ContainerDataType<T, U>
}

export type AssignmentType<T extends ContainerType, U> = {
    id: string
    user_id: string
    project_id: string
    status: StatusType
    task: TaskType<T, U>
    submission?: SubmissionType<T, U>
    step?: StepType
    interface_key?: string
    output_history?: {
        expert?: {
            id: string
        }
        task_id?: string
        is_current_user_step?: boolean
        result?: string
        step_type: StepTypes
        step_name: string
        output: ContainerDataType<T, U>
    }[]
}

export type StepType = {
    id: string
    type: StepTypes
    name: string
}

export enum StepTypes {
    Triager = 'TRIAGER',
    Maker = 'MAKER',
    Reviewer = 'REVIEWER',
    QualityCheck = 'QUALITY_CHECK',
    Rework = 'REWORK',
}

export type AssignmentAtStepType<T extends ContainerType, U> = {
    assignment: AssignmentType<T, U>
}
export type StepLevelTaskDetailsType = {
    task_counts: {
        status: StatusType
        count: number
    }[]
    average_time_in_sec: number | null
}
export type SingleValueDataType<T extends ValueType> = {
    value: ValueTypeDefinitions[T]
    value_type: T
}

export type ValueDataType<T extends ValueType = 'STRING'> = {
    value_id?: string
    data: SingleValueDataType<T>
}

// TODO - Rename to ValueDataType after we migrate from the old ValueDataType type
export type UpdatedValueDataType<T extends ValueType = 'STRING'> = {
    value_id: string
    value: ValueTypeDefinitions[T]
    value_type: T
}

export type CommentType = {
    step_type: string
    content: ValueDataType<'STRING'>
}

export type CommentsType = {
    schema_version: number
    comments: CommentType[]
}

export type DecisionType = {
    schema_version: number
    status: DecisionStatusType
    supporting_comment?: ValueDataType<'STRING'> | UpdatedValueDataType<'STRING'>
    step_name?: string
    submission_reasons?: string[]
}

export type RatingsOnResponseVersionType = {
    TONE: ValueDataType<'STRING'>
}

export type RatingsOnResponseVersionIdType = {
    [versionId: string]: RatingsOnResponseVersionType
}

export type ContainerDataType<T extends ContainerType, U> = {
    id?: string
    is_mutable?: boolean
    input_container_id?: string
    prev_container_id?: string | null
    data: {
        type: T
        schema_version: number
        data: U
    }
    supplement?: SupplementDataType
    decision?: DecisionType | null
    created_at?: string
    updated_at?: string
}

//-----------------------------

type TaskletCountType = {
    status: StatusType
    count: number
}

export type ScopeType = {
    project: {
        id: string
        name: string
        description: string
        nickname: string
    }
    workflow: {
        id: string
        name: string
        use_case: string
    }
    step: {
        id: string
        name: string
        type: string
    }
    task_counts: TaskletCountType[]
    on_hold_task_count: number
    average_time_in_sec: number
}

export type AssignmentScopeType = {
    scopes: ScopeType[]
}

export type CreateSubmissionType = {
    task_submission: {
        id: string
        created_at?: string
        updated_at?: string
        assignment_id: string
        current_output_container_id?: string
        time_elapsed_in_seconds?: number
        status?: StatusType
        extra_info?: {
            type: string
            message: string
        }
    }
}

export enum TaskStatusLabelType {
    TasksAvailable = 'Tasks available',
    TasksCompleted = 'Tasks completed',
    TasksSavedForLater = 'Tasks saved for later',
    AverageTimeTakenForTask = 'Avg time taken per task',
}

export type TaskStatusType = {
    label: string
    value: number | string
}

export type AssignmentScopeResponseType = {
    stepId: string
    projectName: string
    projectId: string
    workflowUseCase: string
    projectNickname: string
    stepType: string
    stepName: string
    projectDescription: string
    useCase: string
    taskCount?: number
}

export type OnHoldAssignmentScopeResponseType = {
    scopes: OnHoldScopeType[]
}

export type OnHoldScopeType = {
    project: {
        id: string
        name: string
        description: string
        nickname: string
    }
    workflow: {
        id: string
        name: string
        use_case: UseCaseTypes
    }
    step: {
        id: string
        name: string
        type: TaskMode
    }
    task_count: number
}

export type GetOnHoldTaskDetailsResponseDTO = {
    step: {
        id: string
        name: string
        type: string
    }
    workflow: {
        id: string
        name: string
        use_case: string
        pipeline: string
    }
    project: {
        id: string
        name: string
        nick_name: string
    }
    in_progress_task_id: string | null
    on_hold_tasks: OnHoldTask[]
}

export type OnHoldTask = {
    task_id: string
    job_id: string
    submission_id: string
    saved_reason: string
    task_status: StatusType
    last_saved_at: string
}

export type OnHoldTaskType = {
    taskId: string
    jobId: string
    submissionId: string
    savedReason: string
    taskStatus: StatusType
    lastSavedAt: string
}

export type GetOnHoldTaskDetailsResponseType = {
    step: {
        id: string
        name: string
        type: string
    }
    workflow: {
        id: string
        name: string
        useCase: string
        pipeline: string
    }
    project: {
        id: string
        name: string
        nickname: string
    }
    inProgressTaskId: string | null
    onHoldTasks: OnHoldTaskType[]
}

export type TaskPromptType = {
    value_type: ValueType
    value: PromptDataType
}

export type NotificationPropsType = {
    type: 'success' | 'error'
    message: string
}

export type TaskResponseType<T extends ContainerType, U> = {
    user_id: string
    output_start_state_container?: ContainerDataType<T, U>
    submission: SubmissionType<T, U>
}

export type TaskConfigType<T extends ContainerType, U = unknown> = {
    assignment: AssignmentType<T, U>
    submissionId?: string
    mode: TaskMode
    jobId?: string
    steps?: StepType[]
    childJobs?: { id: string }[]
    outputs?: JobTasksOutput<U>
}

export type TaskAdditionalInfo = {
    taskId: string
    projectId: string
    assignedUserId: string
    stepId?: string
    useCase: UseCaseTypes
    version?: number
}

export enum TaskMode {
    Maker = 'MAKER',
    Reviewer = 'REVIEWER',
    Viewer = 'VIEWER',
    TaskViewer = 'TASK VIEWER',
    QC = 'QUALITY_CHECK',
    Triager = 'TRIAGER',
    Rework = 'REWORK',
}

export type ErrorAndWarningCount = {
    errorCount: number
    warningCount: number
}

export enum AttributeModalType {
    Radio = 'radio',
    TextArea = 'textarea',
}

export type ModalPropsType<KMetadata = void, TPayload = void> = {
    isVisible: boolean
    onClose: (action?: 'submit' | 'dismiss', payload?: TPayload) => void
    getVisibility: () => boolean
    metaData?: KMetadata
}

export type TextAnnotationType = {
    id: string
    label: string
    color: string
}

export type UserFeedbackRow = {
    task_id: string
    submission_time: string
    result: string
    feedback_comment: string
    job_id: string
    step_type: string
}
export type UserTaskFeedback = {
    feedback_details: UserFeedbackRow[]
}

export type UserJobHistory = {
    count: number
    jobs: {
        jobId: string
        submittedAt: string
        role: string
        workflow_id: string
    }[]
}

export type ClientUserJobHistoryResponseDTO = {
    count: number
    jobs_list: {
        job_id: string
        submitted_at: string
        role: string
        workflow_id: string
    }[]
}

export type UserProjectHistoryResponseDTO = {
    projects: {
        projectId: string
        projectName: string
        workflows: { id: string; name: string }[]
    }[]
}

export type ClientUserProjectHistoryResponseDTO = {
    project_workflows: {
        project_id: string
        project_name: string
        workflows: { id: string; name: string }[]
    }[]
}

export type JobTasksOutput<U> = {
    taskOutput: {
        taskId: string
        stepType: StepTypes
        isCurrentUserStep: boolean
        stepName: string
        result: string
        output: ContainerDataType<ContainerType, U>
    }[]
}

export type ClientJobTasksOutputResponseDTO<U> = {
    task_output:
        | {
              task_id: string
              step_type: StepTypes
              is_current_user_step: boolean
              step_name: string
              result: string
              output: ContainerDataType<ContainerType, U>
          }[]
        | null
}

export type FeedbackFilterType = {
    projectId?: string
    workflowId?: string
    id?: string
}

export type SavedForLaterFilterType = {
    id?: string
}

export type SearchByIdOptionType = 'jobId' | 'taskId'

export type TurnData = {
    llm_response: string
    steps: {
        content: string
        number: number
    }[]
}

export type CustomGenerateResponsePayload = {
    domain?: string
    subDomain?: string
    timeToDevelopStrategy?: number
    timeToSolve?: number
    difficultyLevel?: string
    complexityLevel?: string
    accuracy?: string
    turnDataType: TurnDataType
    humanFinalAnswer?: string
    humanFinalAnswerReasoning?: string
    humanCompleteFinalAnswer?: string
    modelReasoningTrace?: string
    modelReasoningTakenFrom?: string
    modelFinalAnswer?: string
    modelFeedbackDetails?: {
        feedbackResultCode: number
        feedbackUsefulness: string
        feedbackUsefulnessJustification?: string
    }[]
}

export type CustomSubmitTurnDataPayload = {
    type: string
    data: {
        turn_number: number
        response_annotation: {
            accuracy: string
            instruction_following: string
            skill_check: string
            first_incorrect_step: number | null
            skill_gaps: string
        }
        human_final_answer: string
        step_annotations:
            | {
                  step_number: number
                  accuracy: boolean
                  python_or_text: string
                  error_identification: string | null
                  error_correction: string | null
              }[]
            | null
    }
}

export type HumanResponseDataPayload = {
    type: string
    data: {
        steps: {
            accuracy: string
            step_number: number
            content: string
            error_identification: string | null
            error_correction: string | null
        }
        human_final_answer: string
    }
}

export type GenerateResponseFormType = {
    accuracy: string
    domain: string
    subDomain: string
    timeToDevelopStrategy: number
    timeToSolve: number
    difficultyLevel: DifficultyLevel
    complexityLevel: string
    humanFinalAnswer?: string
    humanFinalAnswerReasoning?: string
    humanCompleteFinalAnswer?: string
    modelReasoningTrace?: string
    modelReasoningTakenFrom?: string
    modelFinalAnswer?: string
    modelFeedbackDetails?: {
        feedbackResultCode: number
        feedbackUsefulness: string
        feedbackUsefulnessJustification?: string
    }[]
}

export type ManualResponseFormType = {
    humanCompleteFinalAnswer: string
    humanFinalAnswerReasoning: string
    modelReasoningTrace: string
    modelFinalAnswer: string
    accuracy: string
    modelFeedbackDetails?: {
        feedbackResultCode: number
        feedbackUsefulness: string
        feedbackUsefulnessJustification?: string
    }[]
}

export type PrimaryResponseFormType = {
    isCorrect: boolean
    followInstructions: boolean
    skillsUsed: string
    skillsGap: string
}

export type TurnResponseFormType = {
    human_final_answer: string
    values: {
        step_number: number
        accuracy: string
        python_or_text: string
        error_identification: string | null
        error_correction: string | null
    }[]
}

export type HumanResponseFormType = {
    values: {
        python_or_llm: string
        correct_step: string
    }[]
    human_final_answer: string
}

export type PromptGenerationFormType = {
    prompt: string
    options?: string[]
    contextDocumentUrl: string
    context: string
    externalModelName: string
    externalModelResponse: string
}

export type PromptGenerationFromContextFormType = {
    prompt: string
    context: string
    contextDocumentUrl: string
    contextDocumentName: string
    contextDocumentPageNumber: { pageNumber: string }[]
}

export type PromptAnnotationsType = {
    difficulty: string
    complexity: string
    accuracy: string
    domain: string
    sub_domain: string
    time_for_strategy: number
    time_to_solve: number
    human_final_answer?: string
    human_final_answer_reasoning?: string
    human_complete_final_answer?: string
    model_reasoning_trace?: string
    model_reasoning_taken_from?: string
    model_final_answer?: string
    feedback_level_metrics?: {
        feedback_usefulness: string
        feedback_result_code: number
        feedback_usefulness_justification?: string
    }[]

    //will be used for lmf project
    answer_explanation?: string
    context_document_url?: string
}

export type CustomOutputContainerType = {
    value_type: 'JSON'
    value: {
        final_short_answer: string
        human_correct_response?: {
            steps: {
                content: string
                python_or_text: string
                step_number: number
            }[]
        }
        external_model_response: string | undefined
        external_model_name: string | undefined
        prompt_annotation: PromptAnnotationsType
        turns: {
            llm_response: string
            annotation_data: {
                response_annotation: {
                    accuracy: string
                    first_incorrect_step: number | null
                    instruction_following: string
                    skill_check: string
                    skill_gaps: string
                }
                steps_annotation: {
                    accuracy: string
                    error_correction: string | null
                    error_identification: string | null
                    python_or_text: string
                    step_number: number
                }[]
            }
            steps: {
                content: string
                number: number
            }[]
        }[]
        prompt?: string
        options?: string[]
        context?: string
        context_document_url?: string
        context_document_name?: string
        context_document_page_numbers?: string
        prompt_revisions?: {
            prompt: string
            steps_annotation: string
            feedback_from_model?: ModelFeedback[]
        }[]
    }
}

export type CustomDataType = {
    response: {
        id: string
        internal_data: unknown
        is_complete: boolean
        main_info: CustomOutputContainerType
        extra_info?: UpdatedAttributesData
        version_id: string | null
    }
    quality_check: {
        evaluation_on_response_version_id: {
            [versionId: string]: UpdatedQCAttributesData
        } | null
    }
}

export type TurnDataType = 'PROMPT_ANNOTATION' | 'PROMPT_FINALIZATION'

export type WrappedValueContainer<T> = {
    [name: string]: {
        data: {
            value: T
        }
    }
}

export type FlatValueContainer<T> = {
    [name: string]: {
        value: T
    }
}

export type CommonAnalyticsObject = {
    project_id: string
    task_id: string
    user_id: string
    step_id: string | undefined
}

export type ConversationalTurnDataType = {
    prompt: string
    modelResponse: string
    promptRounds: string[]
}

export type Conversationalv2TurnDataType = {
    prompt: string
    media_files: FileToModelValueType[]
    modelResponse: JSONObject
    promptRounds: object[]
    error?: string
}

export type SubmissionPlayTurnAPIResponseType = {
    submission: {
        output_container: {
            data: {
                schema_version: number
                type: 'MODEL_CONVERSATIONS_OUTPUT'
                data: {
                    response: {
                        id: string
                        extra_info: AttributesData
                        main_info: {
                            value_type: 'JSON'
                            value: {
                                turns: {
                                    external_model_response: string
                                    prompt: string
                                    prompt_rounds: {
                                        responses_list: string[]
                                    }[]
                                }[]
                                turn_annotations: {
                                    attributes: AttributesData
                                }[]
                            }
                        }
                        version_id: null
                        is_complete: boolean
                    }
                    quality_check: {
                        evaluation_on_response_version_id: {
                            [versionId: string]: UpdatedQCAttributesData
                        } | null
                    }
                }
            }
            supplement?: SupplementDataType
            decision?: DecisionType | null
        }
    }
}

export type SubmissionPlayTurnV2APIResponseType = {
    submission: {
        output_container: {
            data: {
                schema_version: number
                type: 'MODEL_CONVERSATIONS_OUTPUT'
                data: {
                    response: {
                        id: string
                        extra_info: AttributesData
                        main_info: {
                            value_type: 'JSON'
                            value: {
                                turns_data: {
                                    prompt: string
                                    external_model_response: JSONObject
                                    retry_responses: object[]
                                    media_files: {
                                        download_url: string
                                        path: string
                                        file_id: string
                                    }[]
                                }[]
                                json_attributes: ConversationJSONAnnotationData
                            }
                        }
                        version_id: null
                        is_complete: boolean
                    }
                    quality_check: {
                        evaluation_on_response_version_id: {
                            [versionId: string]: UpdatedQCAttributesData
                        } | null
                    }
                }
            }
            supplement?: SupplementDataType
            decision?: DecisionType | null
        }
    }
}

export type ConversationalConfigs = {
    turns: number
    retryCount: number
}

export enum ConversationalTurnTypes {
    Conversation = 'PROMPT_MODEL_CONVERSATION',
    ConversationNewTurn = 'PROMPT_MODEL_CONVERSATION_NEW_TURN',
    ConversationFinalization = 'PROMPT_MODEL_CONVERSATION_FINALIZATION',
    PromptRegeneration = 'PROMPT_MODEL_CONVERSATION_REGENERATION',
    PromptEdit = 'PROMPT_MODEL_CONVERSATION_EDIT',
    DraftFill = 'PROMPT_MODEL_CONVERSATION_DRAFT_FILL',
}

export enum Conversationalv2TurnTypes {
    Conversation = 'PROMPT_MODEL_CONVERSATION_V2_PROMPT_INITIALISATION',
    ConversationNewTurn = 'PROMPT_MODEL_CONVERSATION_V2_NEXT_TURN',
    ConversationFinalization = 'PROMPT_MODEL_CONVERSATION_V2_FINALIZATION',
    PromptRegeneration = 'PROMPT_MODEL_CONVERSATION_V2_RESP_REGENERATION',
    PromptEdit = 'PROMPT_MODEL_CONVERSATION_V2_PROMPT_EDIT',
    PromptAutoSave = 'AUTO_SAVE',
    DraftFill = 'PROMPT_MODEL_CONVERSATION_V2_DRAFT_FILL',
}

export type JobHierarchy = {
    parent_job_id: string | null
    child_jobs: { id: string }[]
}

export type MCPTool = {
    mcp_tool_id: string
    name: string
    version_id: string
    workflow_id: string
    is_mutable: boolean
    details: {
        description: string
        inputSchema: JSONObject
    }
}

export type ToolCallResponseType = {
    tool_response: {
        jsonrpc: string
        id: string
        result: {
            isError: boolean
            content: [
                {
                    type: string
                    text: string
                },
            ]
        }
    }
}

export type SubmitTurnDataPayload = {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    data: object
    extraInfo?: AttributesData
    supplement?: SupplementDataType
    turnAnnotations?: {
        attributes?: AttributesData
        json_attributes?: ConversationJSONAnnotationData
    }
    promptIndex?: number
    pluginData?: Record<string, unknown>
}
